import { Map } from 'immutable'
import React from 'react'
import { Action, AsyncState, IOWidget, mk_widget, Pair } from 'widgets-for-react'
import { ImageSize, Link } from '../../../../cms/blocks/types.gen';
import { getClassName, link } from '../../../../cms/blocks/utils.gen'
import { ImageContainer } from '../../../../cms/blocks/widgets/ImageContainer.gen';
import { TemplateState } from '../../../../cms/page/types.gen'
import { CustomBlock } from '../../../../wordpress/types_and_parser/custom_blocks.gen'
import { Block } from '../../../../wordpress/types_and_parser/standard_blocks.gen'
import { Acf } from '../../../../wordpress/types_and_parser/types.mod';
import { PublicSite_CustomPageData } from '../../../application.mod'
import { CustomBlockData } from '../types.mod'

export type BSLShowcaseBlockProps<customBlockData, extra_images> = {
  title: string
  subtitle: string
  items: Array<BSLShowcaseItemProps<extra_images>>
  link: Link
}

export type BSLShowcaseItemProps<extra_images> = {
  key?: string
  title: string
  description?: Acf.Wysiwyg
  image: number
  image_size: ImageSize<extra_images>
  image_alt: string
  link: Link | null
  get_default_data_src: () => ImageSize<extra_images>
  get_default_data_srcset: () => Array<Pair<ImageSize<extra_images>, string>>
}

export type BSLShowcaseBlockStyle = Partial<{
  block_classname: string
  titles_wrapper_classname: string
  subtitle_classname: string
  title_classname: string
  footer_classname: string
  clickable_classname: string
  item_normal_classname: string
  item_large_classname: string
  item_content_classname: string
  item_link_classname: string
  item_subtitle_classname: string
  item_title_large_classname: string
  item_title_normal_classname: string
  item_title_text_classname: string
  item_description_classname: string
  item_faux_link_classname: string
  item_figure_classname: string
  item_image_classname: string
}>

export function BSLShowcaseBlock<customBlockData, extra_images>(
  props: BSLShowcaseBlockProps<customBlockData, extra_images>,
  options?: { style?: BSLShowcaseBlockStyle; key?: string }
): IOWidget<
  TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>,
  Action<TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>>
> {
  return (s: TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>) =>
    mk_widget({
      run: _ => {
        const showcaseItem = (item: BSLShowcaseItemProps<extra_images>, index: number, size: 'normal' | 'large') => {
          return (
            <div
              key={options.key && item && item.key ? `${options.key}-${item.key}` : ''}
              className={
                size === 'large'
                  ? getClassName(options, `item_large_classname`, '')
                  : getClassName(options, `item_normal_classname`, '')
              }
            >
              {link(
                item.link,
                { classname: getClassName(options, 'item_link_classname', '')},
                <>
                  <figure className={getClassName(options, 'item_figure_classname', ``)}>
                    {s.images.has(item.image)
                      ? ImageContainer(
                          s.images.get(item.image) as AsyncState<Map<ImageSize<extra_images>, string>>,
                          item.image_size,
                          getClassName(options, 'item_image_classname', 'w-100'),
                          item.image_alt,
                          item.get_default_data_src,
                          item.get_default_data_srcset
                        ).run(_ => {})
                      : null}
                  </figure>
                  <div className={getClassName(options, 'item_content_classname', '')}>
                    <h3
                      className={
                        size === 'large'
                          ? getClassName(options, `item_title_large_classname`, '')
                          : getClassName(options, `item_title_normal_classname`, '')
                        }
                    >
                      <span className={getClassName(options, 'item_title_text_classname', '')}>{item.title}</span>
                    </h3>
                    {item.description && (
                      <div
                        className={getClassName(options, 'item_description_classname', '')}
                        dangerouslySetInnerHTML={{ __html: item.description }}
                      />
                    )}
                    {size === 'large' && (
                      <span className={getClassName(options, 'item_faux_link_classname', '')}>{item.link.title}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          )
        }

        return (
          <section
            className={getClassName(options, 'block_classname', 'block')}
            key={options && options.key ? options.key : ''}
          >
            <div className={getClassName(options, 'titles_wrapper_classname', 'block')}>
              <h3 className={getClassName(options, 'subtitle_classname', '')}>{props.subtitle}</h3>
              <h2 className={getClassName(options, 'title_classname', '')}>{props.title}</h2>
            </div>
            {props.items.map((item: BSLShowcaseItemProps<extra_images>, index) => {
              return props.items.length === 2 || index === 0
                ? showcaseItem(item, index, 'large')
                : showcaseItem(item, index, 'normal')
            })}
            {props.link && (
              <footer className={getClassName(options, 'footer_classname', '')}>
                {link(props.link, { classname: getClassName(options, 'clickable_classname', '') }, props.link.title)}
              </footer>
            )}
          </section>
        )
      }
    })
}
